import React from 'react';

import dayjs from 'dayjs';
import { Control, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import EntityFormField from 'src/features/membership/components/forms/EntityFormField';
import { CustomMembershipFormSchemaValues } from 'src/features/membership/formSchema';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { DURATION_UNIT } from 'src/features/membershipType/types';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import { useListRoles } from 'src/features/role/api/useListRoles';
import { useListSites } from 'src/features/sites/api/useListSites';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import { Checkbox } from 'src/registry/ui/Checkbox';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/registry/utils/messages';

type CustomMembershipFormProps = {
    form: UseFormReturn<CustomMembershipFormSchemaValues>;
    onSubmit: (values: CustomMembershipFormSchemaValues) => void;
    duration?: number | null;
    durationUnit?: DURATION_UNIT | null;
};

type CheckboxFormFieldProps = {
    control: Control<CustomMembershipFormSchemaValues>;
    fieldName: keyof CustomMembershipFormSchemaValues;
    title: { id: string; defaultMessage: string };
    disabled?: boolean;
};

function CheckboxFormField({ control, fieldName, title, disabled = false }: CheckboxFormFieldProps) {
    return (
        <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
                <FormItem variant="dialog">
                    <FormLabel variant="dialog">
                        <FormattedMessage {...title} />
                    </FormLabel>
                    <FormControl>
                        <Checkbox disabled={disabled} checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                </FormItem>
            )}
        />
    );
}

function CustomMembershipForm({ onSubmit, form, durationUnit, duration }: CustomMembershipFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: sitesData } = useListSites({ parkingId });
    const { data: pricingPlansData } = useListPricingPlans({
        parkingId,
        currently_active: true,
        status: PRICING_PLAN_STATUS.PUBLISHED,
    });
    const { data: rolesData } = useListRoles({ parkingId });

    const { has_pricing_plan, allow_extra_visitors, has_allowed_spaces_count, valid_from } = form.watch();

    React.useEffect(() => {
        if (!has_pricing_plan) {
            form.setValue('apply_pricing_per_space', false);
            form.setValue('auto_renew', false);
            form.setValue('pricing_plan_identity', undefined);
        }
        if (!has_allowed_spaces_count) {
            form.setValue('allow_extra_visitors', false);
            form.setValue('max_extra_visitors_count', undefined);
        }
        if (!allow_extra_visitors) form.setValue('max_extra_visitors_count', undefined);
    }, [allow_extra_visitors, form, has_pricing_plan, has_allowed_spaces_count]);

    React.useEffect(() => {
        if (!durationUnit || !duration || !valid_from) return;

        const newValidTo = dayjs(valid_from).add(duration, durationUnit).toISOString();
        form.setValue('valid_to', newValidTo);
    }, [form, duration, durationUnit, valid_from]);

    const siteOptions = React.useMemo(() => {
        const filteredEmptySiteTranslations = sitesData?.results?.filter((site) => site.translations.length > 0) ?? [];

        return filteredEmptySiteTranslations?.map((site) => {
            const translatedSite = site.translations.find((s) => s.language === locale);

            return { value: site.id, label: translatedSite?.name };
        });
    }, [sitesData, locale]);

    const pricingPlanOptions = React.useMemo(() => {
        const filteredEmptyPricingPlanTranslations =
            pricingPlansData?.results?.filter((plan) => plan.translations.length > 0) ?? [];

        return filteredEmptyPricingPlanTranslations?.map((plan) => {
            const translatedPlan = plan.translations.find((p) => p.language === locale);

            return { value: plan.identity, label: translatedPlan?.name };
        });
    }, [pricingPlansData, locale]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <FormField
                control={form.control}
                name="site"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...membershipMessages.site} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(membershipMessages.site)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {siteOptions?.map((option) => (
                                            <SelectItem key={option?.value} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <Separator />
            <EntityFormField form={form} />
            <Separator />
            <CheckboxFormField
                control={form.control}
                fieldName="has_pricing_plan"
                title={membershipTypeMessages.hasPricingPlan}
            />
            <FormField
                control={form.control}
                name="pricing_plan_identity"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required={!!has_pricing_plan}>
                            <FormattedMessage {...membershipMessages.pricingPlan} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={!has_pricing_plan}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(membershipMessages.pricingPlan)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {pricingPlanOptions?.map((option) => (
                                            <SelectItem key={option?.value} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <CheckboxFormField
                control={form.control}
                fieldName="apply_pricing_per_space"
                title={membershipTypeMessages.applyPricingPerSpace}
                disabled={!has_pricing_plan}
            />

            <CheckboxFormField
                control={form.control}
                fieldName="auto_renew"
                title={membershipTypeMessages.autoRenew}
                disabled={!has_pricing_plan}
            />
            <Separator />

            <CheckboxFormField
                control={form.control}
                fieldName="limit_duration"
                title={membershipTypeMessages.limitDuration}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabled={!!(duration && durationUnit)}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <Separator />

            <InputFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="max_allowed_spaces_count"
                label={formatMessage(membershipMessages.maxAllowedSpaces)}
                type="number"
                min={0}
            />

            <CheckboxFormField
                control={form.control}
                fieldName="has_allowed_spaces_count"
                title={membershipTypeMessages.limitSpaces}
            />

            <CheckboxFormField
                control={form.control}
                fieldName="allow_extra_visitors"
                title={membershipTypeMessages.extraVisitors}
                disabled={!has_allowed_spaces_count}
            />

            <InputFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="max_extra_visitors_count"
                type="number"
                required={!!allow_extra_visitors}
                disabled={!allow_extra_visitors}
                label={formatMessage(membershipTypeMessages.maxExtraVisitors)}
                min={0}
            />

            <Separator />

            <CheckboxFormField
                control={form.control}
                fieldName="allow_access_over_capacity_limit"
                title={membershipTypeMessages.allowAccessOverCapacityLimit}
            />

            <CheckboxFormField
                control={form.control}
                fieldName="allow_access_as_client_outside_restrictions"
                title={membershipTypeMessages.allowAccessAsClientOutsideRestrictions}
            />

            <CheckboxFormField
                control={form.control}
                fieldName="contribute_to_available_spaces"
                title={membershipTypeMessages.contributeToAvailableSpaces}
            />

            <MultiSelectFormField<CustomMembershipFormSchemaValues>
                form={form}
                name="limit_to_roles"
                required={false}
                label={formatMessage(membershipTypeMessages.limitToRoles)}
                options={rolesData?.results}
            />
        </Form>
    );
}

export default CustomMembershipForm;
